import { useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    CloudArrowUp, FileText, CheckCircle, WarningCircle, X, Files, Info,
} from "@phosphor-icons/react";

/**
 * Drag-and-drop bulk paper uploader.
 * Filenames must follow the Cambridge convention: {code}_{s|w|m|y}{yy}_{qp|ms|sp|er|in|ci}_{paper}.pdf
 * (e.g. 9709_s24_qp_12.pdf, 0580_w23_ms_43.pdf).
 *
 * Flow: user drops files → we POST to /api/admin/resources/bulk-parse to preview
 * → user confirms → POST to /api/admin/resources/bulk-upload.
 */
export default function BulkUploader({ onDone }) {
    const [files, setFiles] = useState([]);         // File[] the user has queued
    const [preview, setPreview] = useState([]);      // parse results from backend
    const [previewing, setPreviewing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [result, setResult] = useState(null);     // {uploaded, failed, total}
    const inputRef = useRef(null);

    const addFiles = useCallback((newFiles) => {
        const arr = Array.from(newFiles).filter(f =>
            f.type === "application/pdf" ||
            f.type === "application/msword" ||
            f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
            /\.(pdf|docx?|)$/i.test(f.name)
        );
        if (arr.length !== newFiles.length) {
            toast.warning(`Skipped ${newFiles.length - arr.length} non-PDF/DOC file(s).`);
        }
        setFiles((prev) => {
            const existing = new Set(prev.map(f => f.name));
            return [...prev, ...arr.filter(f => !existing.has(f.name))];
        });
        setPreview([]);   // invalidate the preview until user re-parses
        setResult(null);
    }, []);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };

    const parseFiles = async () => {
        if (files.length === 0) return toast.error("Add some files first.");
        setPreviewing(true);
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        try {
            const { data } = await api.post("/admin/resources/bulk-parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data.files);
            const ok = data.files.filter(f => f.ok).length;
            const bad = data.files.length - ok;
            toast.success(`Parsed ${data.files.length} filenames · ${ok} ready · ${bad} need attention`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Preview failed");
        } finally {
            setPreviewing(false);
        }
    };

    const uploadAll = async () => {
        if (preview.length === 0) return toast.error("Preview first, then upload.");
        setUploading(true);
        const okFilenames = new Set(preview.filter(p => p.ok).map(p => p.filename));
        if (okFilenames.size === 0) {
            setUploading(false);
            return toast.error("No files with a valid Cambridge filename to upload.");
        }
        const fd = new FormData();
        files.filter(f => okFilenames.has(f.name)).forEach(f => fd.append("files", f));
        try {
            const { data } = await api.post("/admin/resources/bulk-upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Uploaded ${data.uploaded.length} of ${data.total} papers`);
            if (onDone) onDone(data.uploaded);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk upload failed");
        } finally {
            setUploading(false);
        }
    };

    const removeFile = (name) => {
        setFiles((prev) => prev.filter(f => f.name !== name));
        setPreview((prev) => prev.filter(p => p.filename !== name));
    };

    const reset = () => {
        setFiles([]);
        setPreview([]);
        setResult(null);
    };

    const okCount = preview.filter(p => p.ok).length;
    const badCount = preview.length - okCount;

    return (
        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="bulk-uploader">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                        <Files size={14} weight="duotone"/> Bulk uploader
                    </div>
                    <div className="font-serif text-2xl text-ink">Drop 100+ Cambridge papers in one go.</div>
                    <div className="text-sm text-ink/60 mt-1 max-w-2xl">
                        Filenames must follow the Cambridge convention:{" "}
                        <code className="font-mono text-xs bg-parchment px-1.5 py-0.5 rounded-sm">{"{code}_{s|w|m|y}{yy}_{qp|ms|sp}_{paper}.pdf"}</code>
                        {" — e.g. "}
                        <code className="font-mono text-xs bg-parchment px-1.5 py-0.5 rounded-sm">9709_s24_qp_12.pdf</code>
                    </div>
                </div>
            </div>

            {/* Drop zone */}
            <div
                onDrop={onDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => inputRef.current?.click()}
                data-testid="bulk-dropzone"
                className={`mt-4 cursor-pointer border-2 border-dashed rounded-sm p-10 text-center transition-colors ${
                    dragOver ? "border-ink bg-parchment/60" : "border-ink/25 hover:border-ink/50 bg-parchment/20"
                }`}
            >
                <CloudArrowUp size={44} weight="duotone" className="mx-auto text-ink/60 mb-3"/>
                <div className="font-serif text-lg text-ink">Drop PDFs here, or click to select</div>
                <div className="text-xs text-ink/50 mt-1">PDF, DOC, DOCX · up to 25 MB each · unlimited files</div>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => e.target.files && addFiles(e.target.files)}
                    data-testid="bulk-file-input"
                />
            </div>

            {/* Queue */}
            {files.length > 0 && !result && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm text-ink/70">
                            <span className="font-mono text-ink">{files.length}</span> file{files.length === 1 ? "" : "s"} queued
                            {preview.length > 0 && (
                                <>
                                    <span className="mx-2 text-ink/30">·</span>
                                    <span className="text-emerald-700 font-mono">{okCount} ready</span>
                                    {badCount > 0 && (
                                        <>
                                            <span className="mx-2 text-ink/30">·</span>
                                            <span className="text-oxblood font-mono">{badCount} won&apos;t upload</span>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={reset} className="rounded-sm h-9" data-testid="btn-bulk-clear">Clear</Button>
                            {preview.length === 0 ? (
                                <Button onClick={parseFiles} disabled={previewing} className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-9" data-testid="btn-bulk-preview">
                                    {previewing ? "Reading filenames…" : "Preview parsed metadata"}
                                </Button>
                            ) : (
                                <Button onClick={uploadAll} disabled={uploading || okCount === 0} className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-9" data-testid="btn-bulk-upload">
                                    <CloudArrowUp size={14} className="mr-2"/>
                                    {uploading ? "Uploading…" : `Upload ${okCount} paper${okCount === 1 ? "" : "s"}`}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* File list — no preview yet */}
                    {preview.length === 0 && (
                        <ul className="divide-y divide-ink/10 border border-ink/10 rounded-sm max-h-72 overflow-y-auto">
                            {files.map(f => (
                                <li key={f.name} className="flex items-center justify-between px-3 py-2 text-sm">
                                    <span className="flex items-center gap-2 truncate">
                                        <FileText size={14} className="text-ink/50 shrink-0"/>
                                        <span className="font-mono text-xs text-ink truncate">{f.name}</span>
                                        <span className="text-xs text-ink/40 whitespace-nowrap">· {Math.round(f.size / 1024)} KB</span>
                                    </span>
                                    <button onClick={() => removeFile(f.name)} className="p-1 hover:bg-ink/5 rounded-sm">
                                        <X size={12} className="text-ink/50"/>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Parsed preview */}
                    {preview.length > 0 && (
                        <div className="border border-ink/10 rounded-sm overflow-hidden">
                            <div className="max-h-96 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-parchment/50 text-xs uppercase tracking-widest text-ink/60 sticky top-0">
                                        <tr>
                                            <th className="text-left px-3 py-2 w-6"></th>
                                            <th className="text-left px-3 py-2">File</th>
                                            <th className="text-left px-3 py-2">Subject</th>
                                            <th className="text-left px-3 py-2">Type</th>
                                            <th className="text-left px-3 py-2">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-ink/10">
                                        {preview.map((p, i) => (
                                            <tr key={i} className={p.ok ? "" : "bg-oxblood/5"} data-testid={`preview-row-${i}`}>
                                                <td className="px-3 py-2">
                                                    {p.ok
                                                        ? <CheckCircle size={16} weight="fill" className="text-emerald-700"/>
                                                        : <WarningCircle size={16} weight="fill" className="text-oxblood"/>}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs text-ink/80 truncate max-w-[220px]">{p.filename}</td>
                                                {p.ok ? (
                                                    <>
                                                        <td className="px-3 py-2 text-ink">
                                                            <span className="font-mono text-xs text-ink/60">{p.subject_code}</span>{" "}
                                                            <span>{p.subject_name}</span>
                                                            <div className="text-[10px] uppercase tracking-widest text-gold-dark">{p.subject_board}</div>
                                                        </td>
                                                        <td className="px-3 py-2 text-xs uppercase tracking-wider text-ink/70">
                                                            {p.paper_type.replace("_", " ")}
                                                        </td>
                                                        <td className="px-3 py-2 text-ink/80">
                                                            <div className="font-serif">{p.title}</div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td colSpan={3} className="px-3 py-2 text-oxblood text-xs italic">
                                                        {p.reason}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Result summary */}
            {result && (
                <div className="mt-6 space-y-4" data-testid="bulk-result">
                    <div className="border border-emerald-700/30 bg-emerald-50 rounded-sm p-4">
                        <div className="flex items-center gap-2 text-emerald-800 font-serif text-xl">
                            <CheckCircle size={20} weight="fill"/> {result.uploaded.length} of {result.total} papers uploaded
                        </div>
                        {result.failed.length > 0 && (
                            <div className="mt-3 text-sm text-ink/70">
                                <div className="flex items-center gap-2 text-oxblood mb-1">
                                    <WarningCircle size={14} weight="fill"/> {result.failed.length} skipped:
                                </div>
                                <ul className="text-xs list-disc pl-5 space-y-0.5">
                                    {result.failed.map((f, i) => (
                                        <li key={i}><span className="font-mono">{f.filename}</span> — {f.reason}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={reset} variant="outline" className="rounded-sm" data-testid="btn-bulk-again">Upload more</Button>
                    </div>
                </div>
            )}

            {/* Hint */}
            {files.length === 0 && !result && (
                <div className="mt-4 flex items-start gap-2 text-xs text-ink/50">
                    <Info size={14} className="mt-0.5 shrink-0"/>
                    <div>
                        Filename codes: <b>s</b>=May/June, <b>w</b>=Oct/Nov, <b>m</b>=Feb/March, <b>y</b>=Specimen.
                        Types: <b>qp</b>=question paper, <b>ms</b>=mark scheme, <b>sp</b>=specimen, <b>er</b>=examiner report,
                        <b> in</b>=insert. Unrecognised filenames stay in the queue but are skipped on upload.
                    </div>
                </div>
            )}
        </div>
    );
}
